/**
 * The shareholder's landing page — 17 Sep 2026.
 *
 * What we want an owner to see when they open the door: a box to write to us
 * in, the places to follow us, and what holding shares is worth in the app.
 * The numbers — the holding, the cap table, the documents — are one tab over,
 * because somebody who comes back weekly is not coming back for the cap table.
 *
 * ## The copy lives here, and that is deliberate
 *
 * Links, store pages and discount codes are constants at the top of this file
 * rather than a settings document with an editor behind it. There are eight
 * strings; an admin screen to maintain them would be more code than the strings,
 * and a code that changes twice a year is a deploy, not a feature. When that
 * stops being true they move to `skidaCrmSettings`, and this is the note that it
 * was considered.
 */
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { callFunction } from '@/shared/firebase/callFunction'
import {
  AppStoreMark,
  FacebookMark,
  GooglePlayMark,
  InstagramMark,
  LinkedInMark,
  YouTubeMark,
} from '@/shared/ui/SocialMarks'

type Mark = (props: { size?: number }) => JSX.Element

/** Where we are. Changing one of these is a deploy — see the note above. */
const FOLLOW: { label: string; href: string; Mark: Mark }[] = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/skida-alpine-adventures',
    Mark: LinkedInMark,
  },
  { label: 'Instagram', href: 'https://www.instagram.com/skida.app/', Mark: InstagramMark },
  { label: 'Facebook', href: 'https://www.facebook.com/skidaapp', Mark: FacebookMark },
  { label: 'YouTube', href: 'https://www.youtube.com/@skida_app', Mark: YouTubeMark },
]

const STORES: { label: string; href: string; Mark: Mark }[] = [
  {
    label: 'App Store',
    href: 'https://apps.apple.com/no/app/skida-alpine-adventures/id6476298275',
    Mark: AppStoreMark,
  },
  {
    label: 'Google Play',
    href: 'https://play.google.com/store/apps/details?id=ski.mountainsnow.skidaalpineadventuresapp',
    Mark: GooglePlayMark,
  },
]

const CODES: { code: string; what: string }[] = [
  { code: 'SKIDASHAREHOLDERS50', what: '50% off Premium — shareholders only' },
  { code: 'SKIDAVIP20', what: '20% off for friends and family' },
]

/** The code, applied. Typing it in is a step nobody has to take. */
const redeemUrl = (code: string) =>
  `https://app.skida.app/map/?code=${encodeURIComponent(code)}`

export function ShareholderHome() {
  return (
    <>
      {/* Follow us first: it is the thing on this page we most want an owner to
          act on, and a page is read from the top. */}
      <section className="outside__card">
        <h2 className="outside__title">Follow us</h2>
        <p className="outside__lede">
          Everything we ship, and most of what we are thinking about, turns up in one of these
          first.
        </p>
        <div className="shx__links">
          {FOLLOW.map(({ label, href, Mark }) => (
            <a className="shx__link" key={label} href={href} target="_blank" rel="noreferrer">
              <Mark />
              {label}
            </a>
          ))}
        </div>

        <div className="card-label outside__sublabel">Give us a review</div>
        <p className="outside__lede">
          Ratings are how a small app gets found. Five stars only, please — you own part of the
          company, so we are not going to pretend to be impartial about it.
        </p>
        <div className="shx__links">
          {STORES.map(({ label, href, Mark }) => (
            <a className="shx__link" key={label} href={href} target="_blank" rel="noreferrer">
              <Mark />
              {label}
            </a>
          ))}
        </div>
      </section>

      <FeedbackCard />

      <section className="outside__card">
        <h2 className="outside__title">Your treats as a shareholder</h2>
        <p className="outside__lede">
          Open the link on the device you ski with, or scan the code with its camera — either way
          the discount is already applied when the app opens.
        </p>
        <div className="shx__codes">
          {CODES.map((c) => (
            <PerkCard key={c.code} code={c.code} what={c.what} />
          ))}
        </div>
        <p className="outside__lede">
          Premium also opens the discounts we have arranged with our partners.
        </p>
      </section>
    </>
  )
}

/**
 * One discount: what it is, the link that applies it, the code, and a QR.
 *
 * All four because the same person reaches it three ways — reading this on a
 * laptop and skiing with a phone, reading it on the phone itself, or typing the
 * code into a paywall they are already looking at. The QR is for the first,
 * which is the case a link alone does not solve.
 */
function PerkCard({ code, what }: { code: string; what: string }) {
  const url = redeemUrl(code)
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let live = true
    // Drawn locally, as the business card's is: same picture, no request, and
    // no third party told which shareholder looked at which discount.
    QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 320,
      color: { dark: '#000B38', light: '#FFFFFF' },
    })
      .then((d) => live && setQr(d))
      .catch(() => live && setQr(null))
    return () => {
      live = false
    }
  }, [url])

  return (
    <div className="shx__perk">
      <div className="shx__perkmain">
        <div className="shx__codewhat">{what}</div>

        <button
          className="shx__codebtn"
          title="Copy the code"
          onClick={() => {
            // `navigator.clipboard` is absent on an insecure origin and in older
            // browsers. The code is on screen either way, so a failure falls back
            // to reading it rather than to a broken button.
            void navigator.clipboard
              ?.writeText(code)
              .then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              })
              .catch(() => undefined)
          }}
        >
          <code>{code}</code>
          <span className="shx__copied">{copied ? 'Copied' : 'Copy'}</span>
        </button>

        <a className="btn btn--primary shx__redeem" href={url} target="_blank" rel="noreferrer">
          Open Skida with the discount
        </a>
      </div>

      {qr && (
        <figure className="shx__qr">
          <img src={qr} alt={`QR code for ${code}`} />
          <figcaption className="micro">Scan with your phone</figcaption>
        </figure>
      )}
    </div>
  )
}

/**
 * One box, one button.
 *
 * Deliberately not a form with a subject, a category and a priority. The point
 * is that writing to us costs nothing — a shareholder with an idea in a lift
 * queue should be able to send it before the chair reaches the top.
 */
function FeedbackCard() {
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!message.trim()) return
    setBusy(true)
    setError(null)
    setSent(null)
    try {
      const out = await callFunction<{ note?: string }>('crmShareholderFeedback', {
        method: 'POST',
        body: { message },
      })
      setMessage('')
      setSent(out.note ?? 'Sent.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The message could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="outside__card">
      <h2 className="outside__title">Tell us something</h2>
      <p className="outside__lede">
        A comment, an idea, somebody we ought to be talking to. It reaches the team, and a copy
        reaches you.
      </p>

      <textarea
        className="input shx__message"
        rows={5}
        value={message}
        maxLength={5000}
        placeholder="What is on your mind?"
        onChange={(e) => setMessage(e.target.value)}
        disabled={busy}
      />

      <div className="shx__send">
        <button
          className="btn btn--primary"
          onClick={() => void submit()}
          disabled={busy || !message.trim()}
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
        {sent && <span className="shx__ok">{sent}</span>}
      </div>

      {error && <p className="modal__error">{error}</p>}
    </section>
  )
}
